/* eslint-disable arrow-body-style */
// @flow
import React, { useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { t } from 'javascripts/utils/withTranslation';
import { hierarchy } from 'd3-hierarchy';
import Icon from 'components/_particles/icon/react/Icon';
import IconLink from 'components/atoms/icon-link/react/IconLink';
import {
  AnimatePresence,
  motion,
  MotionConfig,
  useReducedMotion,
} from 'framer-motion/dist/framer-motion';
import useMediaquery from 'javascripts/hooks/useMediaquery';
import { MEDIA_QUERIES } from 'javascripts/constants';
import type { Item, Tree, Wording } from '../types';
import { printTileValue } from './Tile';
import ListSwitch from './ListSwitch';

type Props = {
  data: Tree,
  inputValue?: number,
  variant?: 'default' | 'calculator',
  wording?: Wording,
  setDisplay: () => void,
  display: 'diagram' | 'list',
};

const ListItem = ({
  item,
  variant,
  onClick,
  formattedValue,
  formattedPercentage,
  index,
  isZero,
  wording,
}: {
  item: Tree,
  variant: 'absolute' | 'relative',
  onClick: () => void,
  formattedValue: string,
  formattedPercentage: string,
  index: number,
  wording?: Wording,
  isZero?: boolean,
}): React$MixedElement => {
  const ref = useRef<HTMLButtonElement | null>(null);
  const shouldReduceMotion = useReducedMotion();

  const handlleItemClick = (event: any) => {
    ref.current?.click();
  };

  const getAnimationValue = (percentage?: number) => {
    const minValue = 0.8;
    let value = Math.max((percentage || 0) * 100, minValue);

    if (isZero) {
      value = minValue;
    }

    return value;
  };

  return (
    <motion.li
      key={`animation-key-${item.data.id}`}
      className="tax-calculator__list-item"
      style={{ cursor: item.children ? 'pointer' : 'default' }}
      onClick={item.children ? handlleItemClick : null}
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{
        duration: 0.3,
        delay: shouldReduceMotion ? 0 : 0.1 + index * 0.1,
      }}
      layoutScroll
    >
      {item?.data?.icon && (
        <div className="tax-calculator__list-icon">
          <Icon icon={item.data.icon} />
        </div>
      )}
      <div
        className={classNames(
          'tax-calculator__list-item-number',
          isZero && 'tax-calculator__currency--empty'
        )}
      >
        {isZero ? wording?.tile?.zero : formattedValue}
      </div>
      <div className="tax-calculator__list-item-title">{item.data.title}</div>
      {item.children && (
        <div className="tax-calculator__arrow">
          <button
            type="button"
            className="button button--icon button--chromeless tax-calculator__arrow-button"
            onClick={onClick}
            aria-expanded={false}
            aria-controls={`sublist--${item.data.id}`}
            ref={ref}
          >
            <Icon icon="arrow-right-line" />
            <span className="u-hidden-visually">
              {t('Betrag weiter aufschlüsseln')}
            </span>
          </button>
        </div>
      )}
      <AnimatePresence>
        <motion.span
          className="tax-calculator__list-indicator"
          initial={
            shouldReduceMotion
              ? {
                  width: `${getAnimationValue(item.data.originalPercentage)}%`,
                  minWidth: '1.2rem',
                }
              : { width: 0, minWidth: 0 }
          }
          animate={{
            width: `${getAnimationValue(item.data.originalPercentage)}%`,
            minWidth: '1.2rem',
          }}
          transition={{ duration: 1, delay: index * 0.1 }}
        >
          <span className="u-hidden-visually">
            {t('Entspricht')} {formattedPercentage} {t('der Gesamtsumme')}
          </span>
        </motion.span>
      </AnimatePresence>
    </motion.li>
  );
};

const DiagramList = ({
  data,
  variant,
  wording,
  display,
  setDisplay,
}: Props): React$MixedElement => {
  const isTablet = useMediaquery(MEDIA_QUERIES.l);
  const [activeList, setActiveList] = React.useState(data);
  const id = useMemo(() => Math.random().toString(36).substr(2, 9), []);
  const { lang } = document.documentElement || { lang: 'de' };
  const ref = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<HTMLElement | null>(null);

  const handleItemClick = (event: any, item?: Tree) => {
    event.stopPropagation();
    if (item) {
      setActiveList(item);
    }
  };

  useEffect(() => {
    if (activeList?.data?.parent || activeList.parent) {
      frameRef.current?.scrollIntoView({ behavior: 'smooth' });
      ref.current?.focus();
    }
  }, [activeList]);

  return (
    <div className="tax-calculator__frame" aria-live="polite" ref={frameRef}>
      <div className="tax-calculator__controls">
        {isTablet && <ListSwitch onClick={setDisplay} display={display} />}
        {(activeList?.data?.parent || activeList?.parent) && (
          <div className="tax-calculator__back">
            <IconLink
              link={undefined}
              icon="arrow-left-line"
              onClick={(e) => {
                // $FlowFixMe
                handleItemClick(
                  e,
                  // $FlowFixMe
                  activeList?.parent || activeList?.data?.parent
                );
              }}
              ref={ref}
            >
              {t('Zurück')}
            </IconLink>
          </div>
        )}
      </div>
      {(activeList?.data?.parent || activeList?.parent) && (
        <header className="tax-calculator__list-header" id={id}>
          <span className="u-hidden-visually">
            {t('Aufschlüsselung des Betrags:')}
          </span>
          {activeList?.data?.icon && (
            <div className="taxt-calcluator__icon">
              <Icon icon={activeList.data.icon} />
            </div>
          )}
          <div className="tax-calculator__list-item-number">
            {printTileValue(
              activeList,
              variant === 'calculator' ? 'relative' : 'absolute',
              lang
            ) ===
            printTileValue(
              undefined,
              variant === 'calculator' ? 'relative' : 'absolute',
              lang
            )
              ? wording?.tile?.zero || t('Wert zu gering')
              : printTileValue(
                  activeList,
                  variant === 'calculator' ? 'relative' : 'absolute',
                  lang
                )}
          </div>
          <h3 className="tax-calculator__list-item-title">
            {activeList.data?.title}
          </h3>
          <div className="tax-calculator__list-item-subtitle">
            {activeList.data?.text}
          </div>

          {activeList?.data?.link && (
            <IconLink
              link={activeList.data.link.url}
              icon="arrow-right-line"
              className="tax-calculator__link"
              target={activeList.data.link.target}
              rel={activeList.data.link.target === '_blank' ? 'noopener' : ''}
            >
              {activeList.data.link.text}
            </IconLink>
          )}

          <button
            type="button"
            onClick={(e) => {
              // $FlowFixMe
              handleItemClick(
                e,
                // $FlowFixMe
                activeList?.parent || activeList?.data?.parent
              );
            }}
            className="button button--icon button--secondary button--small tax-calculator__toggle-button"
          >
            <Icon icon="bw-cross-large-line" />
            <span className="u-hidden-visually">
              {t('Eine Ebene höher gehen')}
            </span>
          </button>
        </header>
      )}

      {!activeList?.data?.parent && !activeList?.parent && (
        <header className="u-hidden-visually" id={id}>
          <h3 className="u-hidden-visually">
            {t('Aufschlüsselung von Steuergeldern:')}
          </h3>
        </header>
      )}

      <ul
        className="tax-calculator__list"
        id={`sublist--${activeList.data.id}`}
        aria-labelledby={id}
      >
        <AnimatePresence initial={false} mode="wait">
          {activeList.children?.map((child: Tree, index) => {
            return (
              <ListItem
                key={child.data.id}
                item={child}
                variant={variant === 'calculator' ? 'relative' : 'absolute'}
                onClick={(e) => handleItemClick(e, child)}
                formattedValue={printTileValue(
                  child,
                  variant === 'calculator' ? 'relative' : 'absolute',
                  lang
                )}
                formattedPercentage={new Intl.NumberFormat(lang, {
                  style: 'percent',
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                  unitDisplay: 'narrow',
                }).format(child.data.percentage || 0)}
                index={index}
                wording={wording}
                isZero={
                  printTileValue(
                    child,
                    variant === 'calculator' ? 'relative' : 'absolute',
                    lang
                  ) ===
                  printTileValue(
                    undefined,
                    variant === 'calculator' ? 'relative' : 'absolute',
                    lang
                  )
                }
              />
            );
          })}
        </AnimatePresence>
      </ul>
    </div>
  );
};

export default DiagramList;
